'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { getGroupDocument, getGroupTasks } from 'src/api/personal/groupDetail';
import { getGroupDocumentTypes } from 'src/api/admin/documentTypes';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/personal_/groups/$groupId/documents/$documentId')({
	loader: async ({ params }) => {
		const { groupId, documentId } = params;
		const [document, documentTypes, groupTasksResponse] = await Promise.all([
			getGroupDocument(groupId, documentId),
			getGroupDocumentTypes(),
			getGroupTasks(groupId, 'pageSize=500'),
		]);
		return {
			document,
			documentTypes,
			groupTasksResponse,
		};
	},
	component: DocumentDetailPage,
});

function DocumentDetailPage() {
	const { groupId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		document: initialDocument,
		documentTypes: initialDocumentTypes,
		groupTasksResponse: initialGroupTasksResponse,
	} = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: document, refetch: refetchDocument } = useQuery({
		queryKey: ['personal-group-document', groupId, documentId],
		queryFn: () => getGroupDocument(groupId, documentId),
		initialData: initialDocument,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['group-document-types'],
		queryFn: () => getGroupDocumentTypes(),
		initialData: initialDocumentTypes,
	});

	const { data: groupTasksResponse } = useQuery({
		queryKey: ['personal-group-tasks', groupId],
		queryFn: () => getGroupTasks(groupId, 'pageSize=500'),
		initialData: initialGroupTasksResponse,
	});

	const groupTasks = groupTasksResponse?.data ?? [];
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleUpdate = () => {
		refetchDocument();
		queryClient.invalidateQueries({ queryKey: ['personal-group-documents', groupId] });
		queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
	};

	const handleClose = () => {
		navigate({
			to: '/personal/groups/$groupId/documents',
			params: { groupId },
			search: { filter: undefined },
		});
	};

	if (!document) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/personal/groups/${groupId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypes?.data || []}
			apiUrlBase={`/api/personal-dashboard/groups/${groupId}/documents`}
			onRevalidateDocument={handleUpdate}
			tasks={groupTasks}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				fullView: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				sensitiveView: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
				edit: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				archive: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				review: false,
			}}
		/>
	);
}
